import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm, ControllerRenderProps, useWatch } from 'react-hook-form';
import {
  DashboardCommerciauxFormSchema,
  RegisterUserStepMutation,
} from './DashboardCommerciauxForm.types';
import { useMutation, useQueryClient } from 'react-query';
import departements from '../../../../../../../assets/json/departements-region.json';
import { createCommercialStepData } from './DashboardCommerciauxForm.data';
import { getCreateCommercialSchema } from './DashboardCommerciauxForm.schema';
import {
  CREATE_COMMERCIAL_QUERY_KEY,
  createCommercialQuery,
} from './DashboardCommerciauxForm.query';
import { useNavigate } from 'react-router-dom';
import SearchInput from '../../../../../../commons/SearchInput/SearchInput';
import { useAddressAutocomplete } from '../../../../../../../hooks/fetchers/useAddressAutocomplete/useAddressAutocomplete';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import {
  AddressSuggestion,
  AddressTypes,
} from '../../../../../../../types/models/address/address.types';
import Input from '../../../../../../commons/Input/Input';
import Button from '../../../../../../designSystem/Button/Button';
import { ButtonSize } from '../../../../../../designSystem/Button/Button.types';
import { extractNumberFromString } from '../../../../../../../utils/helpers/numbers/extractNumberFromString';
import { FormItem } from '../../../../../../commons/Form/Form.types';
import Select from '../../../../../../commons/Select/Select';
import { CommercialTypes } from '../../../../../../../types/api/endpoints/commercial/commercial.types';

export default () => {

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const [searchInputValue, setSearchInputValue] = useState<string>('');

  const { data, refetch } = useAddressAutocomplete(searchInputValue, { types: AddressTypes.City, enabled: false });

  useEffect(() => {
    refetch()
  }, [searchInputValue])

  const formProps = useForm<DashboardCommerciauxFormSchema>({
    resolver: yupResolver(getCreateCommercialSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isValid, errors },
  } = formProps;

  const type = useWatch({
    control,
    name: 'type'
  });

  useEffect(() => {
    setValue('type', CommercialTypes.Employee, { shouldValidate: true })
  }, [])

  useEffect(() => {
    if (type === CommercialTypes.Employee) {
      setValue('siret', undefined)
      setValue('tva', undefined)
    }
  }, [type])

  const { mutate } = useMutation<
    Awaited<ReturnType<RegisterUserStepMutation>>,
    Error,
    DashboardCommerciauxFormSchema
  >((data) => createCommercialQuery(data), {
    mutationKey: CREATE_COMMERCIAL_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: () => {
      navigate(0);
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const onAddressChange = useCallback(
    (
      e: ChangeEvent<HTMLInputElement>,
      onChange: (e: ChangeEvent<HTMLInputElement>) => void
    ) => {
      const value = e.target.value;
      onChange(e);
      setSearchInputValue(value);
    },
    []
  );

  const handleSelectedSuggestion = useCallback(
    (suggestion: AddressSuggestion) => {
      setSearchInputValue('');
      setValue('city', suggestion.city || suggestion.address, { shouldValidate: true });
      setValue(
        'department',
        departements.find(
          (res) =>
            res.num_dep.toString() == suggestion.department_code ||
            (suggestion.short_code && res.num_dep == extractNumberFromString(suggestion.short_code))
        )?.dep_name || ''
      ),
      {
        shouldValidate: true,
      };
      setValue(
        'region',
        departements.find(
          (res) =>
            res.num_dep.toString() == suggestion.department_code ||
            // @ts-ignore
            res.num_dep.toString() == suggestion.region_code ||
            // @ts-ignore
            (suggestion.short_code && res.num_dep == extractNumberFromString(suggestion.short_code))
        )?.region_name || '',
        {
          shouldValidate: true,
        }
      );
    },
    []
  );

  const SearchInputCallback = useCallback(
    (props: FormItem<DashboardCommerciauxFormSchema> & ControllerRenderProps<DashboardCommerciauxFormSchema>) => {
      const { name, placeholder, label, type, value, onChange, ...rest } = props
      return (
        <SearchInput
          suggestions={data}
          placeholder={placeholder || ''}
          type={type}
          onChange={(e) => onAddressChange(e, onChange)}
          error={errors[name]?.message as string}
          {...{ name, value, label, handleSelectedSuggestion }}
          {...rest}
        />
      )
    },
    [data, onAddressChange]
  )

  const AdaptedFormField = useCallback(
    (props: FormItem<DashboardCommerciauxFormSchema> & ControllerRenderProps<DashboardCommerciauxFormSchema>) => {
      const { name, placeholder, label, type, options, value, onChange, onChangeValue, ...rest } = props
      switch (props.name) {
        case 'city':
          return (
            <SearchInputCallback
              placeholder={placeholder || ''}
              type={type}
              onChange={(e) => onAddressChange(e, onChange)}
              error={errors[name]?.message as string}
              {...{ name, value, label, handleSelectedSuggestion }}
              {...rest}
            />
          )
        case 'type':
          return (
            <Select
              placeholder={placeholder || ''}
              error={errors[name]?.message as string}
              {...{ label, options, ...rest }}
              value={{
                value: value as string,
                label: value as string
              }}
              // {...props}
              // @ts-ignore
              onChange={({ value }: SelectOption) => {
                onChange(value);
                if (onChangeValue) {
                  onChangeValue(value);
                }
              }}
            />
          )
        default:
          return (
            <Input
              placeholder={placeholder || ''}
              type={type}
              disabled={Boolean(['department', 'region'].includes(name))}
              error={errors[name]?.message as string}
              {...{ name, onChange, value, label }}
              {...rest}
            />
          )
      }
    },
    []
  )

  AdaptedFormField

  const onSubmit = handleSubmit((data) => mutate(data));

  return (
    <>
      {/* <Form<DashboardCommerciauxFormSchema>
        data={createCommercialStepData}
        {...{ formProps, onSubmit }}
      /> */}
      <form className="form" style={{ overflowY: 'auto' }}>
        {createCommercialStepData.map((row, rowIndex) => (
          <div key={rowIndex} className="form__row">
          {row.map(({ name, placeholder, label, type, onChangeValue, options, ...props }, inputIndex) => (
            <Controller
              key={inputIndex}
              name={name}
              render={({ field: { onChange, value } }) =>
                name === 'city' 
                  ? 
                  (
                  <SearchInput
                    suggestions={data}
                    placeholder={placeholder || ''}
                    type={type}
                    onChange={(e) => onAddressChange(e, onChange)}
                    error={errors[name]?.message as string}
                    {...{ name, value, label, handleSelectedSuggestion }}
                    {...props}
                  />
                ) 
                : 
                  name === 'type'
                  ?
                  <Select
                    placeholder={placeholder || ''}
                    error={errors[name]?.message as string}
                    {...{ label, options, ...props }}
                    value={{
                      value: value as string,
                      label: value as string
                    }}
                    // {...props}
                    // @ts-ignore
                    onChange={({ value }: SelectOption) => {
                      onChange(value);
                      if (onChangeValue) {
                        onChangeValue(value);
                      }
                    }}
                  />
                  :
                  (
                    <Input
                      placeholder={placeholder || ''}
                      type={type}
                      disabled={Boolean(
                        ['department', 'region'].includes(name)
                      )}
                      error={errors[name]?.message as string}
                      {...{ name, onChange, value, label }}
                      {...props}
                    />
                  )
              }
              {...{ control }}
            />
          ))}
            {/* {row.map((input, inputIndex) => (
              <Controller
                key={inputIndex}
                name={input.name}
                render={(renderProps) => {
                  if (type === CommercialTypes.Employee && ['siret', 'tva'].includes(input.name)) {
                    return <></>
                  } else if (input.name === 'city') {
                    return (
                      // @ts-ignore
                      <SearchInputCallback
                        {...input}
                        {...renderProps}
                        onChange={renderProps.field.onChange}
                        value={renderProps.field.value}
                      />
                    )
                  } else {
                    return (
                      // @ts-ignore
                      <AdaptedFormField
                        {...input}
                        {...renderProps}
                        onChange={renderProps.field.onChange}
                        value={renderProps.field.value}
                      />
                    )
                  }
                }}
                {...{ control }}
              />
            ))} */}
          </div>
        ))}
        <Button
          style={{ alignSelf: 'unset' }}
          onClick={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          size={ButtonSize.Large}
          disabled={!isValid}>
          Valider
        </Button>
      </form>
    </>
  );
};
